/* CRA's ESLint loader does not ship the react-hooks/set-state-in-effect rule
   that the platform linter complains about. */

import { useState } from "react";
import { Sparkles, Loader2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORY_PALETTE = {
  behavioral: "border-blue-200 bg-blue-50 text-blue-700",
  technical: "border-emerald-200 bg-emerald-50 text-emerald-700",
  situational: "border-violet-200 bg-violet-50 text-violet-700",
};

/**
 * One-click "Generate from JD" modal for the JobDetail page.
 * Props:
 *   jobId          — the job UUID
 *   onAddQuestions — async (questionsArray) => void. Called when the
 *                    recruiter clicks "Add selected" with the chosen question
 *                    objects (each: { text, category }).
 */
export default function AIQuestionGeneratorModal({ jobId, onAddQuestions }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState([]); // [{text, category, selected}]
  const [adding, setAdding] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    setLoading(true);
    setProposals([]);
    try {
      const { data } = await api.post(`/jobs/${jobId}/generate-questions`, {
        count: 8,
      });
      setProposals(
        (data.questions || []).map((q) => ({ ...q, selected: true }))
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not generate questions"
      );
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/generate-questions`, {
        count: 8,
      });
      setProposals(
        (data.questions || []).map((q) => ({ ...q, selected: true }))
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not regenerate");
    } finally {
      setLoading(false);
    }
  };

  const toggle = (i) =>
    setProposals((p) =>
      p.map((q, idx) => (idx === i ? { ...q, selected: !q.selected } : q))
    );

  const editText = (i, value) =>
    setProposals((p) =>
      p.map((q, idx) => (idx === i ? { ...q, text: value } : q))
    );

  const selectedCount = proposals.filter((q) => q.selected).length;

  const handleAdd = async () => {
    const chosen = proposals
      .filter((q) => q.selected && q.text.trim())
      .map((q) => ({ text: q.text.trim(), category: q.category, listen_for: q.listen_for }));
    if (!chosen.length) return;
    setAdding(true);
    try {
      await onAddQuestions(chosen);
      toast.success(
        `Added ${chosen.length} question${chosen.length === 1 ? "" : "s"}`
      );
      setOpen(false);
    } finally {
      setAdding(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        data-testid="open-ai-question-modal-btn"
        size="sm"
        onClick={handleOpen}
        className="rounded-sm border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100"
      >
        <Sparkles className="mr-2 h-3 w-3" />
        Generate from JD
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="ai-question-modal"
          className="max-w-2xl rounded-sm"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Sparkles className="h-5 w-5 text-violet-600" />
              AI-generated questions
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Pick the ones to add. Edit any question inline before saving.
              Mix of behavioral, technical, and situational questions tuned to
              this role.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div
              data-testid="ai-question-loading"
              className="flex items-center justify-center gap-3 py-12 text-sm text-slate-600"
            >
              <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
              Drafting 8 questions tailored to this JD…
            </div>
          ) : (
            <>
              <ul
                data-testid="ai-question-list"
                className="max-h-[55vh] space-y-2 overflow-y-auto"
              >
                {proposals.map((q, i) => (
                  <li
                    key={i}
                    data-testid={`ai-question-row-${i}`}
                    className={`flex gap-3 rounded-sm border p-3 transition-colors ${
                      q.selected
                        ? "border-slate-300 bg-white"
                        : "border-slate-200 bg-slate-50 opacity-60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`ai-question-toggle-${i}`}
                      checked={q.selected}
                      onChange={() => toggle(i)}
                      className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-violet-600"
                    />
                    <div className="flex-1">
                      <textarea
                        data-testid={`ai-question-text-${i}`}
                        value={q.text}
                        onChange={(e) => editText(i, e.target.value)}
                        rows={2}
                        className="w-full resize-none border-0 bg-transparent text-sm text-slate-900 focus:outline-none focus:ring-0"
                      />
                      <span
                        data-testid={`ai-question-category-${i}`}
                        className={`mt-1 inline-flex items-center border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.2em] ${
                          CATEGORY_PALETTE[q.category] ||
                          "border-slate-200 bg-slate-50 text-slate-700"
                        }`}
                      >
                        {q.category}
                      </span>
                      {q.listen_for && (
                        <p
                          data-testid={`ai-question-listenfor-${i}`}
                          className="mt-2 border-l-2 border-amber-300 bg-amber-50/50 pl-3 text-[11px] italic leading-relaxed text-slate-700"
                        >
                          <strong className="not-italic text-amber-700">Listen for:</strong>{" "}
                          {q.listen_for}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="ai-question-regenerate-btn"
                    onClick={regenerate}
                    className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Sparkles className="mr-1 inline h-3 w-3" />
                    Regenerate all 8
                  </button>
                  <span className="text-xs text-slate-500">
                    {selectedCount} of {proposals.length} selected
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="ai-question-cancel-btn"
                    onClick={() => setOpen(false)}
                    className="rounded-sm"
                  >
                    <X className="mr-2 h-3 w-3" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    data-testid="ai-question-add-selected-btn"
                    onClick={handleAdd}
                    disabled={!selectedCount || adding}
                    className="rounded-sm bg-violet-600 hover:bg-violet-500"
                  >
                    {adding ? (
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    ) : (
                      <Check className="mr-2 h-3 w-3" />
                    )}
                    Add {selectedCount} to job
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
